import "core-js/modules/es6.object.keys";
import "core-js/modules/es6.array.map";
import "core-js/modules/es6.object.create";
import "core-js/modules/es6.promise";
import "core-js/modules/es6.array.for-each";
import "core-js/modules/es6.weak-map";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.string.iterator";
import "core-js/modules/es6.map";
import "core-js/modules/es6.string.link";
import { __awaiter, __generator, __assign } from 'tslib';
import { InvariantError, invariant } from 'ts-invariant';
import Observable from 'zen-observable';
import 'symbol-observable';
import { getDefaultValues, getOperationDefinition, getOperationName } from '../utilities/graphql/getFromAST.js';
import { execute } from '../link/core/execute.js';
import equal from '@wry/equality';
import { hasDirectives, hasClientExports } from '../utilities/graphql/directives.js';
import { graphQLResultHasError, tryFunctionOrLogError } from '../utilities/common/errorHandling.js';
import { removeConnectionDirectiveFromDocument } from '../utilities/graphql/transform.js';
import { canUseWeakMap } from '../utilities/common/canUse.js';
import { isNonEmptyArray } from '../utilities/common/arrays.js';
import { ApolloError, isApolloError } from '../errors/ApolloError.js';
import { MutationStore } from '../data/mutations.js';
import { NetworkStatus, isNetworkRequestInFlight } from './networkStatus.js';
import { FetchType } from './types.js';
import { ObservableQuery } from './ObservableQuery.js';
import { LocalState } from './LocalState.js';
import { multiplex, asyncMap } from '../utilities/observables/observables.js';
import { QueryInfo } from './QueryInfo.js';
var hasOwnProperty = Object.prototype.hasOwnProperty;

var QueryManager = function () {
  function QueryManager(_a) {
    var cache = _a.cache,
        link = _a.link,
        _b = _a.queryDeduplication,
        queryDeduplication = _b === void 0 ? false : _b,
        _c = _a.onBroadcast,
        onBroadcast = _c === void 0 ? function () {
      return undefined;
    } : _c,
        _d = _a.ssrMode,
        ssrMode = _d === void 0 ? false : _d,
        _e = _a.clientAwareness,
        clientAwareness = _e === void 0 ? {} : _e,
        localState = _a.localState,
        assumeImmutableResults = _a.assumeImmutableResults;
    this.mutationStore = new MutationStore();
    this.clientAwareness = {};
    this.idCounter = 1;
    this.queries = new Map();
    this.fetchQueryRejectFns = new Map();
    this.transformCache = new (canUseWeakMap ? WeakMap : Map)();
    this.inFlightLinkObservables = new Map();
    this.pollingInfoByQueryId = new Map();
    this.cache = cache;
    this.link = link;
    this.queryDeduplication = queryDeduplication;
    this.onBroadcast = onBroadcast;
    this.clientAwareness = clientAwareness;
    this.localState = localState || new LocalState({
      cache: cache
    });
    this.ssrMode = ssrMode;
    this.assumeImmutableResults = !!assumeImmutableResults;
  }

  QueryManager.prototype.stop = function () {
    var _this = this;

    this.queries.forEach(function (_info, queryId) {
      _this.stopQueryNoBroadcast(queryId);
    });
    this.fetchQueryRejectFns.forEach(function (reject) {
      reject(process.env.NODE_ENV === "production" ? new InvariantError(14) : new InvariantError('QueryManager stopped while query was in flight'));
    });
  };

  QueryManager.prototype.mutate = function (_a) {
    var mutation = _a.mutation,
        variables = _a.variables,
        optimisticResponse = _a.optimisticResponse,
        updateQueriesByName = _a.updateQueries,
        _b = _a.refetchQueries,
        refetchQueries = _b === void 0 ? [] : _b,
        _c = _a.awaitRefetchQueries,
        awaitRefetchQueries = _c === void 0 ? false : _c,
        updateWithProxyFn = _a.update,
        _d = _a.errorPolicy,
        errorPolicy = _d === void 0 ? 'none' : _d,
        fetchPolicy = _a.fetchPolicy,
        _e = _a.context,
        context = _e === void 0 ? {} : _e;
    return __awaiter(this, void 0, void 0, function () {
      var mutationId, generateUpdateQueriesInfo, optimistic_1, self;

      var _this = this;

      return __generator(this, function (_f) {
        switch (_f.label) {
          case 0:
            process.env.NODE_ENV === "production" ? invariant(mutation, 15) : invariant(mutation, 'mutation option is required. You must specify your GraphQL document in the mutation option.');
            process.env.NODE_ENV === "production" ? invariant(!fetchPolicy || fetchPolicy === 'no-cache', 16) : invariant(!fetchPolicy || fetchPolicy === 'no-cache', "Mutations only support a 'no-cache' fetchPolicy. If you don't want to disable the cache, remove your fetchPolicy setting to proceed with the default mutation behavior.");
            mutationId = this.generateQueryId();
            mutation = this.transform(mutation).document;
            variables = this.getVariables(mutation, variables);
            if (!this.transform(mutation).hasClientExports) return [3, 2];
            return [4, this.localState.addExportedVariables(mutation, variables, context)];

          case 1:
            variables = _f.sent();
            _f.label = 2;

          case 2:
            generateUpdateQueriesInfo = function generateUpdateQueriesInfo() {
              var ret = {};

              if (updateQueriesByName) {
                _this.queries.forEach(function (_a, queryId) {
                  var observableQuery = _a.observableQuery;

                  if (observableQuery && observableQuery.watching) {
                    var queryName = observableQuery.queryName;

                    if (queryName && hasOwnProperty.call(updateQueriesByName, queryName)) {
                      ret[queryId] = {
                        updater: updateQueriesByName[queryName],
                        queryInfo: _this.queries.get(queryId)
                      };
                    }
                  }
                });
              }

              return ret;
            };

            this.mutationStore.initMutation(mutationId, mutation, variables);

            if (optimisticResponse) {
              optimistic_1 = typeof optimisticResponse === 'function' ? optimisticResponse(variables) : optimisticResponse;
              this.cache.recordOptimisticTransaction(function (cache) {
                markMutationResult({
                  mutationId: mutationId,
                  result: {
                    data: optimistic_1
                  },
                  document: mutation,
                  variables: variables,
                  queryUpdatersById: generateUpdateQueriesInfo(),
                  update: updateWithProxyFn
                }, cache);
              }, mutationId);
            }

            this.broadcastQueries();
            self = this;
            return [2, new Promise(function (resolve, reject) {
              var storeResult;
              var error;
              self.getObservableFromLink(mutation, __assign(__assign({}, context), {
                optimisticResponse: optimisticResponse
              }), variables, false).subscribe({
                next: function next(result) {
                  if (graphQLResultHasError(result) && errorPolicy === 'none') {
                    error = new ApolloError({
                      graphQLErrors: result.errors
                    });
                    return;
                  }

                  self.mutationStore.markMutationResult(mutationId);

                  if (fetchPolicy !== 'no-cache') {
                    try {
                      markMutationResult({
                        mutationId: mutationId,
                        result: result,
                        document: mutation,
                        variables: variables,
                        queryUpdatersById: generateUpdateQueriesInfo(),
                        update: updateWithProxyFn
                      }, self.cache);
                    } catch (e) {
                      error = new ApolloError({
                        networkError: e
                      });
                      return;
                    }
                  }

                  storeResult = result;
                },
                error: function error(err) {
                  self.mutationStore.markMutationError(mutationId, err);

                  if (optimisticResponse) {
                    self.cache.removeOptimistic(mutationId);
                  }

                  self.broadcastQueries();
                  reject(new ApolloError({
                    networkError: err
                  }));
                },
                complete: function complete() {
                  if (error) {
                    self.mutationStore.markMutationError(mutationId, error);
                  }

                  if (optimisticResponse) {
                    self.cache.removeOptimistic(mutationId);
                  }

                  self.broadcastQueries();

                  if (error) {
                    reject(error);
                    return;
                  }

                  if (typeof refetchQueries === 'function') {
                    refetchQueries = refetchQueries(storeResult);
                  }

                  var refetchQueryPromises = [];

                  if (isNonEmptyArray(refetchQueries)) {
                    refetchQueries.forEach(function (refetchQuery) {
                      if (typeof refetchQuery === 'string') {
                        self.queries.forEach(function (_a) {
                          var observableQuery = _a.observableQuery;

                          if (observableQuery && observableQuery.watching && observableQuery.queryName === refetchQuery) {
                            refetchQueryPromises.push(observableQuery.refetch());
                          }
                        });
                      } else {
                        var queryOptions = {
                          query: refetchQuery.query,
                          variables: refetchQuery.variables,
                          fetchPolicy: 'network-only'
                        };

                        if (refetchQuery.context) {
                          queryOptions.context = refetchQuery.context;
                        }

                        refetchQueryPromises.push(self.query(queryOptions));
                      }
                    });
                  }

                  Promise.all(awaitRefetchQueries ? refetchQueryPromises : []).then(function () {
                    if (errorPolicy === 'ignore' && storeResult && graphQLResultHasError(storeResult)) {
                      delete storeResult.errors;
                    }

                    resolve(storeResult);
                  });
                }
              });
            })];
        }
      });
    });
  };

  QueryManager.prototype.fetchQuery = function (queryId, options, fetchType, fetchMoreForQueryId) {
    return __awaiter(this, void 0, void 0, function () {
      var _a, fetchPolicy, _b, context, query, variables, storeResult, isNetworkOnly, needToFetch, _c, complete, result, shouldFetch, requestId, queryInfo, networkResult;

      var _this = this;

      return __generator(this, function (_d) {
        switch (_d.label) {
          case 0:
            _a = options.fetchPolicy, fetchPolicy = _a === void 0 ? 'cache-first' : _a, _b = options.context, context = _b === void 0 ? {} : _b;
            query = this.transform(options.query).document;
            variables = this.getVariables(query, options.variables);
            if (!this.transform(query).hasClientExports) return [3, 2];
            return [4, this.localState.addExportedVariables(query, variables, context)];

          case 1:
            variables = _d.sent();
            _d.label = 2;

          case 2:
            options = __assign(__assign({}, options), {
              variables: variables
            });
            isNetworkOnly = fetchPolicy === 'network-only' || fetchPolicy === 'no-cache';
            needToFetch = isNetworkOnly;

            if (!isNetworkOnly) {
              _c = this.cache.diff({
                query: query,
                variables: variables,
                returnPartialData: true,
                optimistic: false
              }), complete = _c.complete, result = _c.result;
              needToFetch = !complete || fetchPolicy === 'cache-and-network';
              storeResult = result;
            }

            shouldFetch = needToFetch && fetchPolicy !== 'cache-only' && fetchPolicy !== 'standby';
            if (hasDirectives(['live'], query)) shouldFetch = true;
            requestId = this.idCounter++;
            queryInfo = this.getQuery(queryId).init({
              document: query,
              variables: variables,
              isPoll: fetchType === FetchType.poll,
              isRefetch: fetchType === FetchType.refetch,
              lastRequestId: requestId
            }).updateWatch(options);
            this.dirty(queryId);
            this.dirty(fetchMoreForQueryId);
            this.setNetStatus(fetchMoreForQueryId, NetworkStatus.fetchMore);

            if (shouldFetch) {
              this.broadcastQueries();
              networkResult = this.fetchRequest({
                requestId: requestId,
                queryId: queryId,
                document: query,
                options: options,
                fetchMoreForQueryId: fetchMoreForQueryId
              })["catch"](function (error) {
                if (isApolloError(error)) {
                  throw error;
                } else {
                  if (requestId >= _this.getQuery(queryId).lastRequestId) {
                    queryInfo.markError(error);

                    _this.setNetStatus(fetchMoreForQueryId, NetworkStatus.ready);

                    _this.dirty(queryId);

                    _this.dirty(fetchMoreForQueryId);

                    _this.broadcastQueries();
                  }

                  throw new ApolloError({
                    networkError: error
                  });
                }
              });

              if (fetchPolicy !== 'cache-and-network') {
                return [2, networkResult];
              }

              networkResult["catch"](function () {});
            }

            this.setNetStatus(queryId, shouldFetch ? queryInfo.networkStatus : NetworkStatus.ready);
            this.dirty(queryId);
            this.dirty(fetchMoreForQueryId);

            if (this.transform(query).hasForcedResolvers) {
              return [2, this.localState.runResolvers({
                document: query,
                remoteResult: {
                  data: storeResult
                },
                context: context,
                variables: variables,
                onlyRunForcedResolvers: true
              }).then(function (result) {
                queryInfo.markResult(result, options, !fetchMoreForQueryId, false);

                _this.broadcastQueries();

                return result;
              })];
            }

            this.broadcastQueries();
            return [2, {
              data: storeResult
            }];
        }
      });
    });
  };

  QueryManager.prototype.getQueryStore = function () {
    var store = Object.create(null);
    this.queries.forEach(function (info, queryId) {
      store[queryId] = {
        variables: info.variables,
        networkStatus: info.networkStatus,
        networkError: info.networkError,
        graphQLErrors: info.graphQLErrors
      };
    });
    return store;
  };

  QueryManager.prototype.getQueryStoreValue = function (queryId) {
    return queryId ? this.queries.get(queryId) : undefined;
  };

  QueryManager.prototype.setNetStatus = function (queryId, status) {
    var queryInfo = queryId && this.getQuery(queryId);

    if (queryInfo) {
      queryInfo.networkStatus = status;

      if (status === NetworkStatus.ready) {
        queryInfo.networkError = null;
      }
    }
  };

  QueryManager.prototype.queryListenerForObserver = function (queryId, observer) {
    return function (info) {
      var observableQuery = info.observableQuery,
          networkStatus = info.networkStatus,
          networkError = info.networkError,
          graphQLErrors = info.graphQLErrors;
      var _a = observableQuery.options,
          fetchPolicy = _a.fetchPolicy,
          _b = _a.errorPolicy,
          errorPolicy = _b === void 0 ? 'none' : _b,
          returnPartialData = _a.returnPartialData,
          partialRefetch = _a.partialRefetch;
      var hasGraphQLErrors = isNonEmptyArray(graphQLErrors);

      if (errorPolicy === 'none' && hasGraphQLErrors || networkError) {
        observer.error && observer.error(new ApolloError({
          graphQLErrors: graphQLErrors,
          networkError: networkError
        }));
        return;
      }

      var diff = info.getDiff();

      if (diff.complete || returnPartialData || partialRefetch || hasGraphQLErrors || fetchPolicy === 'cache-only') {
        var result = {
          data: diff.result,
          loading: isNetworkRequestInFlight(networkStatus),
          networkStatus: networkStatus
        };

        if (errorPolicy === 'all' && hasGraphQLErrors) {
          result.errors = graphQLErrors;
        }

        observer.next && observer.next(result);
      } else if (process.env.NODE_ENV !== 'production' && isNonEmptyArray(diff.missing) && !equal(diff.result, {})) {
        process.env.NODE_ENV === "production" || invariant.warn("Missing cache result fields: " + diff.missing.map(function (m) {
          return m.path.join('.');
        }).join(', '), diff.missing);
      }
    };
  };

  QueryManager.prototype.transform = function (document) {
    var transformCache = this.transformCache;

    if (!transformCache.has(document)) {
      var transformed = this.cache.transformDocument(document);
      var forLink = removeConnectionDirectiveFromDocument(this.cache.transformForLink(transformed));
      var clientQuery = this.localState.clientQuery(transformed);
      var serverQuery = forLink && this.localState.serverQuery(forLink);
      var cacheEntry_1 = {
        document: transformed,
        hasClientExports: hasClientExports(transformed),
        hasForcedResolvers: this.localState.shouldForceResolvers(transformed),
        clientQuery: clientQuery,
        serverQuery: serverQuery,
        defaultVars: getDefaultValues(getOperationDefinition(transformed))
      };

      var add = function add(doc) {
        if (doc && !transformCache.has(doc)) {
          transformCache.set(doc, cacheEntry_1);
        }
      };

      add(document);
      add(transformed);
      add(clientQuery);
      add(serverQuery);
    }

    return transformCache.get(document);
  };

  QueryManager.prototype.getVariables = function (document, variables) {
    return __assign(__assign({}, this.transform(document).defaultVars), variables);
  };

  QueryManager.prototype.watchQuery = function (options, shouldSubscribe) {
    if (shouldSubscribe === void 0) {
      shouldSubscribe = true;
    }

    options = __assign(__assign({}, options), {
      variables: this.getVariables(options.query, options.variables)
    });

    if (typeof options.notifyOnNetworkStatusChange === 'undefined') {
      options.notifyOnNetworkStatusChange = false;
    }

    var observable = new ObservableQuery({
      queryManager: this,
      options: options,
      shouldSubscribe: shouldSubscribe
    });
    this.getQuery(observable.queryId).init({
      document: options.query,
      observableQuery: observable,
      variables: options.variables,
      isPoll: false,
      isRefetch: false
    });
    return observable;
  };

  QueryManager.prototype.query = function (options) {
    var _this = this;

    process.env.NODE_ENV === "production" ? invariant(options.query, 17) : invariant(options.query, 'query option is required. You must specify your GraphQL document ' + 'in the query option.');
    process.env.NODE_ENV === "production" ? invariant(options.query.kind === 'Document', 18) : invariant(options.query.kind === 'Document', 'You must wrap the query string in a "gql" tag.');
    process.env.NODE_ENV === "production" ? invariant(!options.returnPartialData, 19) : invariant(!options.returnPartialData, 'returnPartialData option only supported on watchQuery.');
    process.env.NODE_ENV === "production" ? invariant(!options.pollInterval, 20) : invariant(!options.pollInterval, 'pollInterval option only supported on watchQuery.');
    return new Promise(function (resolve, reject) {
      var watchedQuery = _this.watchQuery(options, false);

      _this.fetchQueryRejectFns.set("query:" + watchedQuery.queryId, reject);

      watchedQuery.result().then(resolve, reject).then(function () {
        return _this.fetchQueryRejectFns["delete"]("query:" + watchedQuery.queryId);
      });
    });
  };

  QueryManager.prototype.generateQueryId = function () {
    return String(this.idCounter++);
  };

  QueryManager.prototype.stopQueryInStore = function (queryId) {
    this.stopQueryInStoreNoBroadcast(queryId);
    this.broadcastQueries();
  };

  QueryManager.prototype.stopQueryInStoreNoBroadcast = function (queryId) {
    var queryInfo = this.queries.get(queryId);
    if (queryInfo) queryInfo.stop();
    this.stopPollingQuery(queryId);
  };

  QueryManager.prototype.addQueryListener = function (queryId, listener) {
    this.getQuery(queryId).listeners.add(listener);
  };

  QueryManager.prototype.clearStore = function () {
    this.fetchQueryRejectFns.forEach(function (reject) {
      reject(process.env.NODE_ENV === "production" ? new InvariantError(21) : new InvariantError('Store reset while query was in flight (not completed in link chain)'));
    });
    this.queries.forEach(function (queryInfo, queryId) {
      if (queryInfo.observableQuery && queryInfo.observableQuery.watching) {
        queryInfo.networkStatus = NetworkStatus.loading;
      } else {
        queryInfo.stop();
      }
    });
    this.mutationStore.reset();
    return this.cache.reset();
  };

  QueryManager.prototype.resetStore = function () {
    var _this = this;

    return this.clearStore().then(function () {
      return _this.reFetchObservableQueries();
    });
  };

  QueryManager.prototype.reFetchObservableQueries = function (includeStandby) {
    var _this = this;

    if (includeStandby === void 0) {
      includeStandby = false;
    }

    var observableQueryPromises = [];
    this.queries.forEach(function (_a, queryId) {
      var observableQuery = _a.observableQuery;

      if (observableQuery && observableQuery.watching) {
        var fetchPolicy = observableQuery.options.fetchPolicy;
        observableQuery.resetLastResults();

        if (fetchPolicy !== 'cache-only' && (includeStandby || fetchPolicy !== 'standby')) {
          observableQueryPromises.push(observableQuery.refetch());
        }

        _this.getQuery(queryId).setDiff(null);
      }
    });
    this.broadcastQueries();
    return Promise.all(observableQueryPromises);
  };

  QueryManager.prototype.observeQuery = function (observableQuery, observer) {
    var queryId = observableQuery.queryId,
        options = observableQuery.options;
    this.getQuery(queryId).observableQuery = observableQuery;

    if (options.pollInterval) {
      this.startPollingQuery(options, queryId);
    }

    this.addQueryListener(queryId, this.queryListenerForObserver(queryId, observer));
    return this.fetchQuery(queryId, options);
  };

  QueryManager.prototype.startGraphQLSubscription = function (_a) {
    var _this = this;

    var query = _a.query,
        fetchPolicy = _a.fetchPolicy,
        variables = _a.variables;
    query = this.transform(query).document;
    variables = this.getVariables(query, variables);

    var makeObservable = function makeObservable(variables) {
      return _this.getObservableFromLink(query, {}, variables, false).map(function (result) {
        if (!fetchPolicy || fetchPolicy !== 'no-cache') {
          if (!graphQLResultHasError(result)) {
            _this.cache.write({
              query: query,
              result: result.data,
              dataId: 'ROOT_SUBSCRIPTION',
              variables: variables
            });
          }

          _this.broadcastQueries();
        }

        if (graphQLResultHasError(result)) {
          throw new ApolloError({
            graphQLErrors: result.errors
          });
        }

        return result;
      });
    };

    if (this.transform(query).hasClientExports) {
      var observablePromise_1 = this.localState.addExportedVariables(query, variables).then(makeObservable);
      return new Observable(function (observer) {
        var sub = null;
        observablePromise_1.then(function (observable) {
          return sub = observable.subscribe(observer);
        }, observer.error);
        return function () {
          return sub && sub.unsubscribe();
        };
      });
    }

    return makeObservable(variables);
  };

  QueryManager.prototype.stopQuery = function (queryId) {
    this.stopQueryNoBroadcast(queryId);
    this.broadcastQueries();
  };

  QueryManager.prototype.stopQueryNoBroadcast = function (queryId) {
    this.stopQueryInStoreNoBroadcast(queryId);
    this.removeQuery(queryId);
  };

  QueryManager.prototype.removeQuery = function (queryId) {
    this.fetchQueryRejectFns["delete"]("query:" + queryId);
    this.fetchQueryRejectFns["delete"]("fetchRequest:" + queryId);
    this.getQuery(queryId).subscriptions.forEach(function (x) {
      return x.unsubscribe();
    });
    this.queries["delete"](queryId);
  };

  QueryManager.prototype.getCurrentQueryResult = function (observableQuery, optimistic) {
    if (optimistic === void 0) {
      optimistic = true;
    }

    var _a;

    var _b = observableQuery.options,
        variables = _b.variables,
        query = _b.query,
        fetchPolicy = _b.fetchPolicy,
        returnPartialData = _b.returnPartialData;
    var lastResult = observableQuery.getLastResult();

    if (fetchPolicy === 'no-cache' || fetchPolicy === 'network-only') {
      var diff = this.getQuery(observableQuery.queryId).getDiff();
      return {
        data: (_a = diff) === null || _a === void 0 ? void 0 : _a.result,
        partial: false
      };
    }

    var _c = this.cache.diff({
      query: query,
      variables: variables,
      previousResult: lastResult ? lastResult.data : undefined,
      returnPartialData: true,
      optimistic: optimistic
    }),
        result = _c.result,
        complete = _c.complete;

    return {
      data: complete || returnPartialData ? result : void 0,
      partial: !complete
    };
  };

  QueryManager.prototype.getQueryWithPreviousResult = function (queryIdOrObservable) {
    var observableQuery;

    if (typeof queryIdOrObservable === 'string') {
      var foundObservableQuery = this.getQuery(queryIdOrObservable).observableQuery;
      process.env.NODE_ENV === "production" ? invariant(foundObservableQuery, 22) : invariant(foundObservableQuery, "ObservableQuery with this id doesn't exist: " + queryIdOrObservable);
      observableQuery = foundObservableQuery;
    } else {
      observableQuery = queryIdOrObservable;
    }

    var _a = observableQuery.options,
        variables = _a.variables,
        query = _a.query;
    return {
      previousResult: this.getCurrentQueryResult(observableQuery, false).data,
      variables: variables,
      document: query
    };
  };

  QueryManager.prototype.broadcastQueries = function () {
    this.onBroadcast();
    this.queries.forEach(function (info) {
      return info.notify();
    });
  };

  QueryManager.prototype.getLocalState = function () {
    return this.localState;
  };

  QueryManager.prototype.getObservableFromLink = function (query, context, variables, deduplication) {
    var _this = this;

    if (deduplication === void 0) {
      deduplication = this.queryDeduplication;
    }

    var observable;
    var serverQuery = this.transform(query).serverQuery;

    if (serverQuery) {
      var _a = this,
          inFlightLinkObservables_1 = _a.inFlightLinkObservables,
          link = _a.link;

      var operation = {
        query: serverQuery,
        variables: variables,
        operationName: getOperationName(serverQuery) || void 0,
        context: this.prepareContext(__assign(__assign({}, context), {
          forceFetch: !deduplication
        }))
      };
      context = operation.context;

      if (deduplication) {
        var byVariables_1 = inFlightLinkObservables_1.get(serverQuery) || new Map();
        inFlightLinkObservables_1.set(serverQuery, byVariables_1);
        var varJson_1 = JSON.stringify(variables);
        observable = byVariables_1.get(varJson_1);

        if (!observable) {
          byVariables_1.set(varJson_1, observable = multiplex(execute(link, operation)));

          var cleanup = function cleanup() {
            byVariables_1["delete"](varJson_1);
            if (!byVariables_1.size) inFlightLinkObservables_1["delete"](serverQuery);
            cleanupSub_1.unsubscribe();
          };

          var cleanupSub_1 = observable.subscribe({
            next: cleanup,
            error: cleanup,
            complete: cleanup
          });
        }
      } else {
        observable = multiplex(execute(link, operation));
      }
    } else {
      observable = Observable.of({
        data: {}
      });
      context = this.prepareContext(context);
    }

    var clientQuery = this.transform(query).clientQuery;

    if (clientQuery) {
      observable = asyncMap(observable, function (result) {
        return _this.localState.runResolvers({
          document: clientQuery,
          remoteResult: result,
          context: context,
          variables: variables
        });
      });
    }

    return observable;
  };

  QueryManager.prototype.fetchRequest = function (_a) {
    var _this = this;

    var requestId = _a.requestId,
        queryId = _a.queryId,
        document = _a.document,
        options = _a.options,
        fetchMoreForQueryId = _a.fetchMoreForQueryId;
    var variables = options.variables,
        _b = options.errorPolicy,
        errorPolicy = _b === void 0 ? 'none' : _b,
        fetchPolicy = options.fetchPolicy;
    var resultFromStore;
    var errorsFromStore;
    return new Promise(function (resolve, reject) {
      var observable = _this.getObservableFromLink(document, options.context, variables);

      var subs = _this.getQuery(queryId).subscriptions;

      var fqrfId = "fetchRequest:" + queryId;

      _this.fetchQueryRejectFns.set(fqrfId, reject);

      var cleanup = function cleanup() {
        _this.fetchQueryRejectFns["delete"](fqrfId);

        subs["delete"](subscription);
      };

      var subscription = observable.map(function (result) {
        var queryInfo = _this.getQuery(queryId);

        if (requestId >= queryInfo.lastRequestId) {
          queryInfo.markResult(result, options, !fetchMoreForQueryId, true);

          _this.setNetStatus(fetchMoreForQueryId, NetworkStatus.ready);

          _this.dirty(queryId);

          _this.dirty(fetchMoreForQueryId);

          _this.broadcastQueries();
        }

        if (errorPolicy === 'none' && isNonEmptyArray(result.errors)) {
          return reject(new ApolloError({
            graphQLErrors: result.errors
          }));
        }

        if (errorPolicy === 'all') {
          errorsFromStore = result.errors;
        }

        if (fetchMoreForQueryId || fetchPolicy === 'no-cache') {
          resultFromStore = result.data;
        } else {
          var _a = _this.cache.diff({
            variables: variables,
            query: document,
            optimistic: false,
            returnPartialData: true
          }),
              result_1 = _a.result,
              complete = _a.complete;

          if (complete || options.returnPartialData) {
            resultFromStore = result_1;
          }
        }
      }).subscribe({
        error: function error(_error) {
          cleanup();
          reject(_error);
        },
        complete: function complete() {
          cleanup();
          resolve({
            data: resultFromStore,
            errors: errorsFromStore,
            loading: false,
            networkStatus: NetworkStatus.ready
          });
        }
      });
      subs.add(subscription);
    });
  };

  QueryManager.prototype.getQuery = function (queryId) {
    if (queryId && !this.queries.has(queryId)) {
      this.queries.set(queryId, new QueryInfo(this.cache));
    }

    return this.queries.get(queryId);
  };

  QueryManager.prototype.dirty = function (queryId) {
    if (queryId) {
      this.getQuery(queryId).setDirty();
    }
  };

  QueryManager.prototype.prepareContext = function (context) {
    if (context === void 0) {
      context = {};
    }

    var newContext = this.localState.prepareContext(context);
    return __assign(__assign({}, newContext), {
      clientAwareness: this.clientAwareness
    });
  };

  QueryManager.prototype.checkInFlight = function (queryId) {
    var query = this.getQueryStoreValue(queryId);
    return !!query && !!query.networkStatus && query.networkStatus !== NetworkStatus.ready && query.networkStatus !== NetworkStatus.error;
  };

  QueryManager.prototype.startPollingQuery = function (options, queryId) {
    var _this = this;

    var pollInterval = options.pollInterval;
    process.env.NODE_ENV === "production" ? invariant(pollInterval, 23) : invariant(pollInterval, 'Attempted to start a polling query without a polling interval.');

    if (!this.ssrMode) {
      var info = this.pollingInfoByQueryId.get(queryId);

      if (!info) {
        this.pollingInfoByQueryId.set(queryId, info = {});
      }

      info.interval = pollInterval;
      info.options = __assign(__assign({}, options), {
        fetchPolicy: 'network-only'
      });

      var maybeFetch_1 = function maybeFetch_1() {
        var info = _this.pollingInfoByQueryId.get(queryId);

        if (info) {
          if (_this.checkInFlight(queryId)) {
            poll_1();
          } else {
            _this.fetchQuery(queryId, info.options, FetchType.poll).then(poll_1, poll_1);
          }
        }
      };

      var poll_1 = function poll_1() {
        var info = _this.pollingInfoByQueryId.get(queryId);

        if (info) {
          clearTimeout(info.timeout);
          info.timeout = setTimeout(maybeFetch_1, info.interval);
        }
      };

      poll_1();
    }

    return queryId;
  };

  QueryManager.prototype.stopPollingQuery = function (queryId) {
    this.pollingInfoByQueryId["delete"](queryId);
  };

  return QueryManager;
}();

function markMutationResult(mutation, cache) {
  if (!graphQLResultHasError(mutation.result)) {
    var cacheWrites_1 = [{
      result: mutation.result.data,
      dataId: 'ROOT_MUTATION',
      query: mutation.document,
      variables: mutation.variables
    }];
    var queryUpdatersById_1 = mutation.queryUpdatersById;

    if (queryUpdatersById_1) {
      Object.keys(queryUpdatersById_1).forEach(function (id) {
        var _a = queryUpdatersById_1[id],
            updater = _a.updater,
            _b = _a.queryInfo,
            document = _b.document,
            variables = _b.variables;

        var _c = cache.diff({
          query: document,
          variables: variables,
          returnPartialData: true,
          optimistic: false
        }),
            currentQueryResult = _c.result,
            complete = _c.complete;

        if (complete) {
          var nextQueryResult = tryFunctionOrLogError(function () {
            return updater(currentQueryResult, {
              mutationResult: mutation.result,
              queryName: getOperationName(document) || undefined,
              queryVariables: variables
            });
          });

          if (nextQueryResult) {
            cacheWrites_1.push({
              result: nextQueryResult,
              dataId: 'ROOT_QUERY',
              query: document,
              variables: variables
            });
          }
        }
      });
    }

    cache.performTransaction(function (c) {
      cacheWrites_1.forEach(function (write) {
        return c.write(write);
      });
      var update = mutation.update;

      if (update) {
        tryFunctionOrLogError(function () {
          return update(c, mutation.result);
        });
      }
    });
  }
}

export { QueryManager };